import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { checkAdminSession, logoutAdmin } from '@/lib/auth';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: 'ri-dashboard-3-line' },
  { path: '/admin/inquiries', label: 'Inquiries', icon: 'ri-mail-unread-line' },
  { path: '/admin/history', label: 'History', icon: 'ri-history-line' },
  { path: '/admin/portfolio', label: 'Portfolio', icon: 'ri-briefcase-4-line' },
  { path: '/admin/content', label: 'Content', icon: 'ri-article-line' },
  { path: '/admin/settings', label: 'Settings', icon: 'ri-settings-4-line' },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const verifyAuth = async () => {
      const user = await checkAdminSession();
      if (!active) return;
      if (!user) {
        navigate('/admin/login', { replace: true });
      } else {
        setUserEmail(user.email);
        setChecking(false);
      }
    };

    verifyAuth();

    return () => {
      active = false;
    };
  }, [navigate, location.pathname]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logoutAdmin();
    navigate('/admin/login', { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-ink-950 text-white flex items-center justify-center">
        <div className="text-sm text-white/60 flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin text-neon-400 text-lg" />
          Verifying Security Credentials...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-950 text-white flex flex-col md:flex-row">
      {/* Desktop Sidebar */}
      <aside className="w-64 border-r border-white/5 bg-ink-900/50 flex-col hidden md:flex shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        <div className="p-6 border-b border-white/5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-neon-600 to-neon-400 text-white text-sm font-bold font-display shadow-[0_0_15px_rgba(139,92,246,0.4)]">
              FC
            </div>
            <div>
              <div className="font-display font-bold text-sm text-white">FormaCode</div>
              <div className="text-[10px] tracking-wider text-neon-300 font-mono">ADMIN PANEL</div>
            </div>
          </Link>
        </div>

        <div className="px-6 py-4 border-b border-white/5 bg-white/[0.02]">
          <div className="text-xs text-white/40">Logged in as</div>
          <div className="text-xs font-mono text-neon-300 truncate mt-0.5" title={userEmail || ''}>
            {userEmail}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === item.path
                ? 'bg-neon-500/15 text-neon-300 border border-neon-500/30 shadow-[0_0_15px_rgba(139,92,246,0.15)]'
                : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
            >
              <i className={`${item.icon} text-lg`} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-xs text-white/50 hover:text-white hover:bg-white/5 transition"
          >
            <i className="ri-external-link-line" />
            View Website
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-white/70 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition w-full"
          >
            <i className="ri-logout-box-r-line text-lg" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Top Header */}
      <header className="md:hidden sticky top-0 z-40 bg-ink-950/95 backdrop-blur-xl border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileDrawerOpen(true)}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition"
            aria-label="Open Admin Menu"
          >
            <i className="ri-menu-line text-xl" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded bg-gradient-to-br from-neon-600 to-neon-400 text-white text-xs font-bold font-display">
              FC
            </div>
            <span className="font-display font-bold text-sm">Admin Panel</span>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
          title="Logout"
        >
          <i className="ri-logout-box-r-line" />
        </button>
      </header>

      {/* Mobile Slide-out Drawer */}
      {mobileDrawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileDrawerOpen(false)}
          />
          <aside className="relative w-4/5 max-w-xs bg-ink-900 border-r border-white/10 flex flex-col h-full z-10 p-6 overflow-y-auto">
            <div className="flex items-center justify-between border-b border-white/5 pb-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-neon-600 to-neon-400 text-white text-sm font-bold font-display">
                  FC
                </div>
                <span className="font-display font-bold text-base text-white">FormaCode</span>
              </div>
              <button
                onClick={() => setMobileDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/60 hover:text-white"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/5">
              <div className="text-[10px] text-white/40">Logged in as</div>
              <div className="text-xs font-mono text-neon-300 truncate mt-0.5">
                {userEmail}
              </div>
            </div>

            <nav className="flex-1 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileDrawerOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === item.path
                    ? 'bg-neon-500/15 text-neon-300 border border-neon-500/30'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <i className={`${item.icon} text-lg`} />
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="pt-4 border-t border-white/5 space-y-2 mt-auto">
              <Link
                to="/"
                onClick={() => setMobileDrawerOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-xs text-white/60 hover:text-white hover:bg-white/5 transition"
              >
                <i className="ri-external-link-line" />
                View Main Website
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-400 bg-red-500/10 border border-red-500/20 transition w-full"
              >
                <i className="ri-logout-box-r-line text-lg" />
                Sign Out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto min-w-0">
        <div className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}